/* eslint-disable no-debugger */
import { useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CLIP_UPDATE_TYPE } from '../../../ClipNewTimeline/clipUpdateDataUtils/constants';
import { updateClipData } from '../../../ClipNewTimeline/clipUpdateDataUtils';
import { getAllSegments } from '../../../ClipNewTimeline/clipUpdateDataUtilsWithSplits/compositeUtils';
import { Effect, EffectType } from '@/App/remotion/MainPlayer/types/Animations/Effects';
import { showErrorToast } from '@/libs/toast/toast';
import { AddMenuItem } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/TimelinePlayerActionBar/AddMenu';
import {
  getSegmentAtCurrentFrame,
  getSegmentTimeFromPlayerTime
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/timeUtils';
import { useRemotionPlayerCurrentTime } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerCurrentTime';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { transcriptTimelineSelectionStore } from '@/stores/transcriptTimelineSelection';
import { SegmentType } from '@/App/remotion/MainPlayer/types/Segment/Type';
import { CompositeSegment } from '@/App/remotion/MainPlayer/types/Segment/CompositeSegment';

interface ZoomEffectParams {
  startTime: number;
  duration: number;
  intensity?: number;
  baseScale?: number;
  toScale?: number;
  centerX?: number;
  centerY?: number;
  animationInDuration?: number;
  animationOutDuration?: number;
  animationType?: 'easeInAndOut' | 'linear' | 'bounce';
}

function createZoomEffect({
  startTime,
  duration,
  intensity = 0.8,
  baseScale = 1.0,
  toScale = baseScale + intensity,
  centerX = 0.5,
  centerY = 0.5,
  animationInDuration = 0.5,
  animationOutDuration = 0.5,
  animationType = 'easeInAndOut'
}: ZoomEffectParams): Effect {
  return {
    id: uuidv4?.() || '',
    type: EffectType.Zoom,
    start: startTime,
    duration,
    params: {
      baseScale,
      toScale,
      centerX,
      centerY
    },
    enabled: true,
    animationInDuration,
    animationOutDuration,
    animationType
  };
}

async function addZoomEffectToTimeline(clipId: string, effect: Effect, compositeId: string): Promise<void> {
  try {
    updateClipData({
      clipId,
      actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_EFFECT,
      effect,
      compositeId
    });
  } catch (error) {
    showErrorToast('Failed to add zoom effect');
    throw error;
  }
}

export const useZoomEffectUpdate = () => {
  const { clipData, clipId, fps } = useClipsContext();
  const { currentTime } = useRemotionPlayerCurrentTime();

  const handleAddZoomEffect = useCallback(async (): Promise<void> => {
    const { startTime, endTime, startSegmentId, endSegmentId } = transcriptTimelineSelectionStore.getSnapshot();

    let effectStartTimeSeconds: number;
    let effectDurationSeconds: number;
    let compositeSegment: CompositeSegment | null = null;
    if (startTime !== null && endTime !== null) {
      if (!startSegmentId || !endSegmentId || startSegmentId !== endSegmentId) {
        return;
      }

      const allSegments = getAllSegments(clipData);
      const currentSegment = allSegments.find(segment => segment.id === startSegmentId);
      if (!currentSegment || currentSegment.type !== SegmentType.COMPOSITE) {
        return;
      }
      compositeSegment = currentSegment;
      effectStartTimeSeconds = startTime;
      effectDurationSeconds = endTime - startTime;
    } else {
      const currentSegment = getSegmentAtCurrentFrame(currentTime, clipData);
      if (!currentSegment || currentSegment.type !== SegmentType.COMPOSITE) {
        return;
      }
      compositeSegment = currentSegment;
      const segmentTime = getSegmentTimeFromPlayerTime(currentTime, clipData, currentSegment.id);
      effectStartTimeSeconds = segmentTime;
      effectDurationSeconds = 5;

      const compositeEndTime = currentSegment.currentBounds.toFrame / fps;
      if (effectStartTimeSeconds + effectDurationSeconds > compositeEndTime) {
        effectDurationSeconds = compositeEndTime - effectStartTimeSeconds;
      }
    }

    const existingZoomEffects = (compositeSegment.effects || [])
      .filter(effect => effect.type === EffectType.Zoom)
      .sort((a, b) => a.start - b.start);

    const nextZoomEffect = existingZoomEffects.find(effect => effect.start > effectStartTimeSeconds);

    if (nextZoomEffect) {
      const durationUntilNextZoom = nextZoomEffect.start - effectStartTimeSeconds;
      effectDurationSeconds = Math.min(effectDurationSeconds, durationUntilNextZoom);
    }

    const zoomEffect = createZoomEffect({
      startTime: effectStartTimeSeconds,
      duration: effectDurationSeconds,
      intensity: 0.1
    });
    addZoomEffectToTimeline(clipId, zoomEffect, compositeSegment.id);
  }, [clipData, currentTime, clipId, fps]);

  const isZoomEffectDisabled = useMemo(() => {
    const currentSegment = getSegmentAtCurrentFrame(currentTime, clipData);
    const compositeSegment = currentSegment && currentSegment.type === SegmentType.COMPOSITE ? currentSegment : null;

    if (!compositeSegment) {
      return true;
    }

    const compositeStartTime = compositeSegment.fromFrame / fps;
    const compositeEndTime = compositeSegment.toFrame / fps;

    if (currentTime < compositeStartTime || currentTime > compositeEndTime) {
      return true;
    }

    return false;
  }, [clipData, currentTime, fps]);

  const addZoomEffectMenuItem = useMemo(
    (): AddMenuItem => ({
      id: 'add-zoom-effect',
      label: 'Add Zoom Effect',
      iconName: 'IconZoomIn' as const,
      onClick: handleAddZoomEffect,
      isDisabled: isZoomEffectDisabled,
      showBetaTag: true
    }),
    [handleAddZoomEffect, isZoomEffectDisabled]
  );

  return {
    addZoomEffectMenuItem
  };
};
